import React, { Component } from "react";
import axios from "../../utils/axiosInstance";
import Swal from "sweetalert2";
import "./Comments.css";

class Comments extends Component {
  state = {
    comments: [],
    customers: [],
    posts: [],
    formData: {
      id: null,
      post_id: "",
      customer_id: "",
      comment_details: "",
      comment_star: "5",
      comment_status: "1",
    },
    deleteId: null,
    error: "",
    loading: false,
    submitting: false,
    deleting: false,
    modalMode: "add",
    searchTerm: "",
    statusFilter: "all",
    currentPage: 1,
    itemsPerPage: 10,
  };

  componentDidMount() {
    this.fetchCustomers();
    this.fetchPosts();
    this.fetchComments();
  }

  fetchComments = async () => {
    this.setState({ loading: true, error: "" });
    try {
      const res = await axios.get("/posts/comments");
      const data = res.data?.data || [];
      this.setState({
        comments: Array.isArray(data) ? data : [],
        currentPage: 1,
      });
    } catch (err) {
      this.setState({
        error: err.response?.data?.message || "Failed to load comments.",
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  fetchCustomers = async () => {
    try {
      const res = await axios.get("/customers");
      this.setState({ customers: res.data?.data || [] });
    } catch (err) {
      console.error("Error fetching customers", err);
    }
  };

  fetchPosts = async () => {
    try {
      const res = await axios.get("/posts");
      this.setState({ posts: res.data?.data || [] });
    } catch (err) {
      console.error("Error fetching posts", err);
    }
  };

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      formData: { ...prev.formData, [name]: value },
      error: "",
    }));
  };

  handleSearchChange = (e) => {
    this.setState({ searchTerm: e.target.value, currentPage: 1 });
  };

  handleStatusFilterChange = (e) => {
    this.setState({ statusFilter: e.target.value, currentPage: 1 });
  };

  handleItemsPerPageChange = (e) => {
    this.setState({ itemsPerPage: Number(e.target.value), currentPage: 1 });
  };

  clearFilters = () => {
    this.setState({ searchTerm: "", statusFilter: "all", currentPage: 1 });
  };

  openModal = (mode = "add", comment = null) => {
    const modalElement = document.getElementById("commentModal");
    if (!modalElement || !window.bootstrap) return;

    const modal = new window.bootstrap.Modal(modalElement);

    this.setState(
      {
        modalMode: mode,
        formData:
          mode === "edit" && comment
            ? {
                id: comment.id,
                post_id: comment.post_id || "",
                customer_id: comment.customer_id || "",
                comment_details: comment.comment_details || "",
                comment_star: comment.comment_star?.toString() || "5",
                comment_status: comment.comment_status?.toString() || "1",
              }
            : {
                id: null,
                post_id: "",
                customer_id: "",
                comment_details: "",
                comment_star: "5",
                comment_status: "1",
              },
        error: "",
      },
      () => modal.show()
    );
  };

  closeModal = () => {
    const modalElement = document.getElementById("commentModal");
    if (modalElement && window.bootstrap) {
      const modal = window.bootstrap.Modal.getInstance(modalElement);
      if (modal) modal.hide();
    }
    this.resetForm();
  };

  resetForm = () => {
    this.setState({
      formData: {
        id: null,
        post_id: "",
        customer_id: "",
        comment_details: "",
        comment_star: "5",
        comment_status: "1",
      },
      error: "",
      submitting: false,
      modalMode: "add",
    });
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const { formData, modalMode } = this.state;

    if (!formData.post_id) return this.setState({ error: "Please select a post." });
    if (!formData.customer_id) return this.setState({ error: "Please select a customer." });
    if (!formData.comment_details.trim()) return this.setState({ error: "Comment details are required." });

    this.setState({ submitting: true, error: "" });

    try {
      if (modalMode === "add") {
        await axios.post("/posts/comments", formData);
      } else {
        await axios.put(`/posts/comments/${formData.id}`, formData);
      }

      await this.fetchComments();
      this.closeModal();

      Swal.fire({
        icon: "success",
        title: modalMode === "add" ? "Comment added!" : "Comment updated!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      this.setState({
        error: err.response?.data?.message || err.response?.data?.error || "Error saving comment.",
      });
    } finally {
      this.setState({ submitting: false });
    }
  };

  confirmDelete = (id) => {
    this.setState({ deleteId: id });
    const modalElement = document.getElementById("deleteModal");
    if (!modalElement || !window.bootstrap) return;

    const modal = new window.bootstrap.Modal(modalElement);
    modal.show();
  };

  handleDelete = async () => {
    const { deleteId } = this.state;
    if (!deleteId) return;

    this.setState({ deleting: true });

    try {
      await axios.delete(`/posts/comments/${deleteId}`);
      await this.fetchComments();

      const modalElement = document.getElementById("deleteModal");
      if (modalElement && window.bootstrap) {
        const modal = window.bootstrap.Modal.getInstance(modalElement);
        if (modal) modal.hide();
      }

      this.setState({ deleteId: null });

      Swal.fire({
        icon: "success",
        title: "Comment deleted!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text: err.response?.data?.message || "Please try again.",
      });
    } finally {
      this.setState({ deleting: false });
    }
  };

  getFilteredComments = () => {
    const { comments, searchTerm, statusFilter } = this.state;
    const keyword = searchTerm.trim().toLowerCase();

    return comments.filter((c) => {
      const text = (c.comment_details || "").toLowerCase();
      const customer = (c.full_name || "").toLowerCase();
      const postTitle = (c.title || "").toLowerCase();
      const status = c.comment_status?.toString() || "0";

      const matchesSearch = !keyword || text.includes(keyword) || customer.includes(keyword) || postTitle.includes(keyword);
      const matchesStatus = statusFilter === "all" || status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  };

  getPageNumbers = (totalPages) => {
    const { currentPage } = this.state;
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);

    const pages = [1];
    if (currentPage > 4) pages.push("...");

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }

    if (currentPage < totalPages - 3) pages.push("...");
    pages.push(totalPages);

    return pages;
  };

  goToPage = (page) => {
    const { currentPage, itemsPerPage } = this.state;
    const totalPages = Math.max(1, Math.ceil(this.getFilteredComments().length / itemsPerPage));
    let nextPage = page;

    if (page === "prev") nextPage = currentPage - 1;
    if (page === "next") nextPage = currentPage + 1;
    if (typeof nextPage !== "number") return;

    nextPage = Math.min(totalPages, Math.max(1, nextPage));
    if (nextPage !== currentPage) this.setState({ currentPage: nextPage });
  };

  renderStars = (count) => {
    const stars = [];
    const rating = Number(count) || 1;
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <i key={i} className={i <= rating ? "ph-fill ph-star active-star" : "ph ph-star empty-star"} />
      );
    }
    return <div className="comment-star-rating">{stars}</div>;
  };

  render() {
    const {
      comments,
      customers,
      posts,
      formData,
      error,
      loading,
      submitting,
      deleting,
      modalMode,
      searchTerm,
      statusFilter,
      currentPage,
      itemsPerPage,
    } = this.state;

    const filteredComments = this.getFilteredComments();
    const totalPages = Math.max(1, Math.ceil(filteredComments.length / itemsPerPage));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const startIndex = (safeCurrentPage - 1) * itemsPerPage;
    const currentComments = filteredComments.slice(startIndex, startIndex + itemsPerPage);
    const startRecord = filteredComments.length ? startIndex + 1 : 0;
    const endRecord = Math.min(startIndex + itemsPerPage, filteredComments.length);

    const activeCount = comments.filter((c) => c.comment_status?.toString() === "1").length;
    const topStarCount = comments.filter((c) => Number(c.comment_star) === 5).length;
    const pageNumbers = this.getPageNumbers(totalPages);

    return (
      <div className="pc-container comments-page">
        <div className="pc-content">
          <div className="comments-shell">
            
            {/* Header */}
            <div className="comments-topbar">
              <div>
                <div className="comments-breadcrumb">
                  <span>Community</span>
                  <i className="ph ph-caret-right" />
                  <strong>Comments & Reviews</strong>
                </div>
                <h2 className="comments-title">Comments</h2>
                <p className="comments-subtitle">
                  Manage customer feedback, reviews, and post engagements.
                </p>
              </div>
              <button
                type="button"
                className="bookly-primary-btn"
                onClick={() => this.openModal("add")}
              >
                <i className="ph ph-chat-centered-text" />
                <span>New Comment</span>
              </button>
            </div>

            {/* Summary Grid */}
            <div className="comment-summary-grid">
              <div className="comment-summary-card">
                <div className="comment-summary-icon blue">
                  <i className="ph ph-chats-circle" />
                </div>
                <div>
                  <span className="summary-label">Total Comments</span>
                  <strong>{comments.length}</strong>
                </div>
              </div>

              <div className="comment-summary-card">
                <div className="comment-summary-icon green">
                  <i className="ph ph-check-circle" />
                </div>
                <div>
                  <span className="summary-label">Active Comments</span>
                  <strong>{activeCount}</strong>
                </div>
              </div>

              <div className="comment-summary-card">
                <div className="comment-summary-icon gold">
                  <i className="ph-fill ph-star" />
                </div>
                <div>
                  <span className="summary-label">5-Star Reviews</span>
                  <strong>{topStarCount}</strong>
                </div>
              </div>
            </div>

            {/* Main Card */}
            <div className="comment-card">
              <div className="comment-card-header">
                <div>
                  <h4>Comment List</h4>
                  <p>
                    {filteredComments.length} result
                    {filteredComments.length !== 1 ? "s" : ""} found
                  </p>
                </div>
              </div>

              {/* Toolbar */}
              <div className="comment-toolbar">
                <div className="comment-search-wrap">
                  <i className="ph ph-magnifying-glass" />
                  <input
                    type="text"
                    className="comment-search"
                    placeholder="Search comments, customers, or posts..."
                    value={searchTerm}
                    onChange={this.handleSearchChange}
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      className="comment-search-clear"
                      onClick={() => this.setState({ searchTerm: "", currentPage: 1 })}
                    >
                      <i className="ph ph-x" />
                    </button>
                  )}
                </div>

                <div className="comment-filter-group">
                  <label htmlFor="statusFilter">Status</label>
                  <select
                    id="statusFilter"
                    className="comment-filter-select"
                    value={statusFilter}
                    onChange={this.handleStatusFilterChange}
                  >
                    <option value="all">All Status</option>
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>

                <div className="comment-filter-group">
                  <label htmlFor="rowsFilter">Rows</label>
                  <select
                    id="rowsFilter"
                    className="comment-filter-select"
                    value={itemsPerPage}
                    onChange={this.handleItemsPerPageChange}
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                  </select>
                </div>

                {(searchTerm || statusFilter !== "all") && (
                  <button
                    type="button"
                    className="comment-reset-btn"
                    onClick={this.clearFilters}
                  >
                    <i className="ph ph-arrow-counter-clockwise" />
                    Reset
                  </button>
                )}
              </div>

              {error && !loading && (
                <div className="comment-alert">
                  <i className="ph ph-warning-circle" />
                  <span>{error}</span>
                  <button type="button" onClick={this.fetchComments}>
                    Retry
                  </button>
                </div>
              )}

              {loading ? (
                <div className="comment-loading">
                  <div className="spinner-border" role="status" aria-hidden="true" />
                  <span>Loading comments...</span>
                </div>
              ) : (
                <>
                  <div className="comment-table-wrap">
                    <table className="comment-table">
                      <thead>
                        <tr>
                          <th className="col-number">#</th>
                          <th className="col-post">Post</th>
                          <th className="col-customer">Customer</th>
                          <th className="col-details">Comment</th>
                          <th className="col-rating">Rating</th>
                          <th className="col-status">Status</th>
                          <th className="col-actions">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentComments.length ? (
                          currentComments.map((c, index) => {
                            const isActive = c.comment_status === "1" || c.comment_status === 1;
                            return (
                              <tr key={c.id}>
                                <td className="col-number table-index">
                                  {startIndex + index + 1}
                                </td>
                                <td className="col-post">
                                  <div className="cell-flex">
                                    <div className="cell-icon bg-light-blue">
                                      <i className="ph ph-article" />
                                    </div>
                                    <span className="truncate-text" title={c.title}>
                                      {c.title || "Unknown Post"}
                                    </span>
                                  </div>
                                </td>
                                <td className="col-customer">
                                  <div className="cell-flex">
                                    <div className="cell-icon bg-light-pink">
                                      <i className="ph ph-user" />
                                    </div>
                                    <span className="truncate-text font-weight-bold" title={c.full_name}>
                                      {c.full_name || "Unknown Customer"}
                                    </span>
                                  </div>
                                </td>
                                <td className="col-details">
                                  <div className="comment-text-preview">
                                    {c.comment_details}
                                  </div>
                                </td>
                                <td className="col-rating">
                                  {this.renderStars(c.comment_star)}
                                </td>
                                <td className="col-status">
                                  <span
                                    className={`comment-status-badge ${
                                      isActive ? "active" : "inactive"
                                    }`}
                                  >
                                    <span className="status-dot" />
                                    {isActive ? "Active" : "Inactive"}
                                  </span>
                                </td>
                                <td className="col-actions">
                                  <div className="comment-actions">
                                    <button
                                      type="button"
                                      className="comment-action-btn edit"
                                      onClick={() => this.openModal("edit", c)}
                                      title="Edit Comment"
                                    >
                                      <i className="ph ph-note-pencil" />
                                    </button>
                                    <button
                                      type="button"
                                      className="comment-action-btn delete"
                                      onClick={() => this.confirmDelete(c.id)}
                                      title="Delete Comment"
                                    >
                                      <i className="ph ph-trash" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="7">
                              <div className="comment-empty-state">
                                <div className="empty-icon">
                                  <i className="ph ph-chat-teardrop-text" />
                                </div>
                                <h5>No comments found</h5>
                                <p>
                                  {searchTerm || statusFilter !== "all"
                                    ? "Try changing your search query or filters."
                                    : "No comments have been added yet."}
                                </p>
                                {searchTerm || statusFilter !== "all" ? (
                                  <button
                                    type="button"
                                    className="comment-reset-btn"
                                    onClick={this.clearFilters}
                                  >
                                    Clear Filters
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="bookly-secondary-btn"
                                    onClick={() => this.openModal("add")}
                                  >
                                    <i className="ph ph-plus" />
                                    Add Comment
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {filteredComments.length > 0 && (
                    <div className="comment-pagination-wrap">
                      <div className="comment-pagination-info">
                        Showing <strong>{startRecord}</strong> to{" "}
                        <strong>{endRecord}</strong> of{" "}
                        <strong>{filteredComments.length}</strong> entries
                      </div>

                      {totalPages > 1 && (
                        <nav aria-label="Comment pagination">
                          <ul className="comment-pagination">
                            <li className={safeCurrentPage === 1 ? "disabled" : ""}>
                              <button
                                type="button"
                                onClick={() => this.goToPage("prev")}
                                disabled={safeCurrentPage === 1}
                              >
                                <i className="ph ph-caret-left" />
                              </button>
                            </li>
                            {pageNumbers.map((page, index) =>
                              page === "..." ? (
                                <li key={`ellipsis-${index}`} className="ellipsis">
                                  <span>...</span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={safeCurrentPage === page ? "active" : ""}
                                >
                                  <button type="button" onClick={() => this.goToPage(page)}>
                                    {page}
                                  </button>
                                </li>
                              )
                            )}
                            <li className={safeCurrentPage === totalPages ? "disabled" : ""}>
                              <button
                                type="button"
                                onClick={() => this.goToPage("next")}
                                disabled={safeCurrentPage === totalPages}
                              >
                                <i className="ph ph-caret-right" />
                              </button>
                            </li>
                          </ul>
                        </nav>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Add/Edit Modal */}
        <div className="modal fade" id="commentModal" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content comment-modal">
              <form onSubmit={this.handleSubmit}>
                <div className="modal-header comment-modal-header">
                  <div>
                    <span className="comment-modal-kicker">
                      {modalMode === "add" ? "Create" : "Update"}
                    </span>
                    <h5 className="modal-title">
                      {modalMode === "add" ? "Add Comment" : "Edit Comment"}
                    </h5>
                  </div>
                  <button
                    type="button"
                    className="comment-modal-close"
                    data-bs-dismiss="modal"
                    onClick={this.resetForm}
                  >
                    <i className="ph ph-x" />
                  </button>
                </div>

                <div className="modal-body comment-modal-body">
                  {error && (
                    <div className="comment-form-alert">
                      <i className="ph ph-warning-circle" />
                      <span>{error}</span>
                    </div>
                  )}

                  <div className="row">
                    <div className="col-md-6">
                      <div className="comment-form-group">
                        <label>Target Post <span>*</span></label>
                        <div className="comment-input-wrap">
                          <i className="ph ph-article" />
                          <select
                            name="post_id"
                            className="comment-input"
                            value={formData.post_id}
                            onChange={this.handleChange}
                            required
                          >
                            <option value="" disabled>Select a post...</option>
                            {posts.map((p) => (
                              <option key={p.id} value={p.id}>{p.title}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="comment-form-group">
                        <label>Customer <span>*</span></label>
                        <div className="comment-input-wrap">
                          <i className="ph ph-user" />
                          <select
                            name="customer_id"
                            className="comment-input"
                            value={formData.customer_id}
                            onChange={this.handleChange}
                            required
                          >
                            <option value="" disabled>Select a customer...</option>
                            {customers.map((c) => (
                              <option key={c.id} value={c.id}>{c.full_name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="comment-form-group">
                    <label>Comment Details <span>*</span></label>
                    <textarea
                      name="comment_details"
                      className="comment-textarea"
                      placeholder="Write the comment or review here..."
                      value={formData.comment_details}
                      onChange={this.handleChange}
                      rows="4"
                      required
                    />
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="comment-form-group mb-md-0">
                        <label>Star Rating</label>
                        <div className="comment-input-wrap">
                          <i className="ph ph-star" />
                          <select
                            name="comment_star"
                            className="comment-input"
                            value={formData.comment_star}
                            onChange={this.handleChange}
                          >
                            {[5, 4, 3, 2, 1].map((n) => (
                              <option key={n} value={n}>{n} Star{n > 1 ? "s" : ""}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="comment-form-group mb-0">
                        <label>Status</label>
                        <div className="comment-status-options">
                          <label className={`status-option ${formData.comment_status === "1" ? "selected" : ""}`}>
                            <input
                              type="radio"
                              name="comment_status"
                              value="1"
                              checked={formData.comment_status === "1"}
                              onChange={this.handleChange}
                            />
                            <span><i className="ph ph-check-circle" /> Active</span>
                          </label>
                          <label className={`status-option ${formData.comment_status === "0" ? "selected" : ""}`}>
                            <input
                              type="radio"
                              name="comment_status"
                              value="0"
                              checked={formData.comment_status === "0"}
                              onChange={this.handleChange}
                            />
                            <span><i className="ph ph-pause-circle" /> Inactive</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer comment-modal-footer">
                  <button
                    type="button"
                    className="bookly-cancel-btn"
                    data-bs-dismiss="modal"
                    onClick={this.resetForm}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bookly-primary-btn"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className={modalMode === "add" ? "ph ph-plus" : "ph ph-check"} />
                        {modalMode === "add" ? "Save Comment" : "Update Comment"}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Delete Modal */}
        <div className="modal fade" id="deleteModal" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered modal-sm">
            <div className="modal-content delete-modal">
              <div className="delete-modal-body">
                <div className="delete-warning-icon">
                  <i className="ph ph-trash" />
                </div>
                <h5>Delete Comment?</h5>
                <p>
                  This comment will be permanently removed. This action cannot be undone.
                </p>
                <div className="delete-modal-actions">
                  <button
                    type="button"
                    className="bookly-cancel-btn"
                    data-bs-dismiss="modal"
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="bookly-danger-btn"
                    onClick={this.handleDelete}
                    disabled={deleting}
                  >
                    {deleting ? (
                      <>
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <i className="ph ph-trash" />
                        Yes, Delete
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Comments;
